import { stagesTermsTextMap } from '~/business-logic/billingLogic';
import { getRecurrencePeriodLabels } from '~/constants';
import { Accounting } from '~/types/api.model';
import { serviceModel } from '..';
import { ServiceStatusType, ServiceSource, EffectiveDateSource } from '../service/model';
import * as serviceBillingModel from '../service-billing';
import { isOngoing } from '../service-billing';
import * as serviceCostModel from '../service-cost';
export function supportsPriceIncrease(param) {
    let { billing } = param;
    return serviceBillingModel.isRecurring(billing);
}
export function id(service) {
    return service.id;
}
export function cost(service) {
    return service.cost;
}
export function discount(service) {
    return serviceCostModel.discount(service.cost);
}
export function expiredDiscount(service) {
    return serviceCostModel.expiredDiscount(service.cost);
}
export function billingTrigger(service) {
    return service.billing;
}
export function recurrencePeriod(billing) {
    return billing.recurrencePeriod;
}
export function billingDayOfMonth(service) {
    return serviceBillingModel.billingDayOfMonth(service.billing);
}
export function billingDayOfWeek(service) {
    return serviceBillingModel.billingDayOfWeek(service.billing);
}
export function billingDayOfYear(service) {
    return serviceBillingModel.billingDayOfYear(service.billing);
}
export function maxChargesEnabled(service) {
    return serviceBillingModel.maxChargesEnabled(service.billing);
}
export function maxCharges(service) {
    return serviceBillingModel.maxCharges(service.billing);
}
export function maxChargesLimit(service) {
    return serviceBillingModel.maxChargesLimit(service.billing);
}
export function name(service) {
    return service.name;
}
export function price(param) {
    let { cost } = param;
    return serviceCostModel.price(cost);
}
export function minPrice(service) {
    return serviceCostModel.minPrice(service.cost);
}
export function unitName(param) {
    let { cost } = param;
    return serviceCostModel.unitName(cost);
}
export function unitCap(param) {
    let { cost } = param;
    return serviceCostModel.unitCap(cost);
}
export function hasPreapprovedAmount(param) {
    let { cost } = param;
    const unitCap = serviceCostModel.unitCap(cost);
    return unitCap != null && unitCap.gt(0);
}
export function finalPrice(param, quantity) {
    let { cost } = param;
    return serviceCostModel.finalPrice(cost, quantity);
}
export function applyDiscount(param) {
    let { cost } = param;
    return serviceCostModel.applyDiscount(cost);
}
export function minPriceFinal(param) {
    let { cost } = param;
    return serviceCostModel.minPriceFinal(cost);
}
export function changes(service) {
    return isAmended(service.status) ? service.status.amended.changes : isCompleted(service.status) ? service.status.completed.changes || null : null;
}
export function isBilledUpfront(service) {
    return serviceBillingModel.isBilledUpfront(service.billing);
}
export function isSkipBillingOnAcceptance(service) {
    return serviceBillingModel.isSkipBillingOnAcceptance(service.billing);
}
/**
 * returns true for cases:
 * - draft -` isBillOnAcceptance`
 * - draft - `isBillOnEffectiveDate`
 *  - edit agreement - `isBillInvoiceImmediately`
 */ export const isBillOnPeriodBeforeNextBilling = (service)=>{
    const startBillingAt = serviceModel.startBillingAt(service);
    if (startBillingAt) {
        return false;
    }
    if (!serviceBillingModel.isOngoing(service.billing)) {
        return false;
    }
    return serviceModel.isBilledUpfront(service) && !serviceModel.isSkipBillingOnAcceptance(service);
};
export const isBillOnAcceptance = isBillOnPeriodBeforeNextBilling;
export const isBillOnEffectiveDate = isBillOnPeriodBeforeNextBilling;
export const isBillInvoiceImmediately = isBillOnPeriodBeforeNextBilling;
export const isQuarterly = (service)=>serviceBillingModel.isQuarterly(service.billing);
export const isMonthly = (service)=>serviceBillingModel.isMonthly(service.billing);
export const isWeekly = (service)=>serviceBillingModel.isWeekly(service.billing);
export const isBiWeekly = (service)=>serviceBillingModel.isBiWeekly(service.billing);
export function isAmended(status) {
    return status.type === ServiceStatusType.Amended;
}
export function isApproved(status) {
    return status.type === ServiceStatusType.Approved;
}
export function isAwaitingApproval(status) {
    return status.type === ServiceStatusType.AwaitingApproval;
}
export function requiresClientApproval(service) {
    return isAmended(service.status) || isAwaitingApproval(service.status);
}
export function isPaused(service) {
    return serviceBillingModel.isPaused(service.billing);
}
export function isProrated(service) {
    return serviceBillingModel.isProrated(service.billing);
}
export function hasAmendments(service) {
    const serviceChanges = changes(service);
    return !!serviceChanges && Object.keys(serviceChanges).some((k)=>serviceChanges[k] != null);
}
export function note(service) {
    return isAmended(service.status) ? service.status.amended.note : isAwaitingApproval(service.status) ? service.status.awaitingApproval.note : null;
}
export function approvalMethod(service) {
    return isAmended(service.status) ? service.status.amended.approvalMethod : isAwaitingApproval(service.status) ? service.status.awaitingApproval.approvalMethod || null : null;
}
export function hasAutoAmendments(service) {
    const apMethod = approvalMethod(service);
    return !!apMethod && apMethod.isAutoApproval();
}
export function hasApprovalMethod(service) {
    return !!approvalMethod(service);
}
export function autoApprovalDate(service) {
    var _apMethod_autoApprovalDate;
    const apMethod = approvalMethod(service);
    return apMethod === null || apMethod === void 0 ? void 0 : (_apMethod_autoApprovalDate = apMethod.autoApprovalDate()) === null || _apMethod_autoApprovalDate === void 0 ? void 0 : _apMethod_autoApprovalDate.getTime();
}
export function isMandatory(participation) {
    return participation.type === 'mandatory';
}
export function isOptional(participation) {
    return participation.type === 'optional';
}
export function isTerminated(status) {
    return status.type === ServiceStatusType.Terminated;
}
export function isCompleted(status) {
    return status.type === ServiceStatusType.Completed;
}
export function isCanceled(status) {
    return status.type === ServiceStatusType.Canceled;
}
export function effectiveDate(param) {
    let { status } = param;
    return isAwaitingApproval(status) || isCanceled(status) ? null : status.date;
}
export function startBillingAt(service) {
    if (!isOngoing(service.billing)) return null;
    return matchStatus(service.status, {
        amended: (status)=>status.amended.effectiveDateSource === EffectiveDateSource.User ? status.amended.effectiveDate : null,
        approved: (status)=>status.approved.effectiveDateSource === EffectiveDateSource.User ? status.approved.effectiveDate : null,
        awaitingApproval: (status)=>status.awaitingApproval.effectiveDateSource === EffectiveDateSource.User ? status.awaitingApproval.effectiveDate : null,
        canceled: ()=>null,
        completed: (status)=>status.completed.effectiveDateSource === EffectiveDateSource.User ? status.completed.effectiveDate : null,
        terminated: ()=>null
    });
}
export function amendmentDate(param) {
    let { status } = param;
    return isAwaitingApproval(status) ? status.awaitingApproval.addedOn : isAmended(status) ? status.amended.amendedOn : null;
}
export function priceIncrease(param) {
    let { cost } = param;
    return serviceCostModel.priceIncrease(cost);
}
export function nextYearPrice(service) {
    return serviceCostModel.nextYearPrice(service.cost);
}
export function integratedClassId(service) {
    return service.integrations.qboIntegratedClassId();
}
export function isIntegratedClassSet(service) {
    return service.integrations.isQboClassSet();
}
export const hasFinancialCentsIntegration = (service)=>Boolean(service.bundle ? service.bundle.services.find((s)=>s.integrations.financialCentsIntegration()) : service.integrations.financialCentsIntegration());
export const hasKarbonIntegration = (service)=>Boolean(service.bundle ? service.bundle.services.find((s)=>s.integrations.karbonIntegration()) : service.integrations.karbonIntegration());
export function isFromAmendment(service) {
    return service.source === ServiceSource.Amendment;
}
export function isSuitableForTimeActivities(service) {
    return serviceBillingModel.isManual(billingTrigger(service)) && serviceCostModel.isVariablePrice(service.cost) && serviceModel.isApproved(service.status);
}
export function isManual(service) {
    return serviceBillingModel.isManual(service.billing);
}
export function formatRecurrencePeriod(service, texts) {
    if (serviceBillingModel.isOneTimeInStages(service.billing)) {
        return texts.inStages();
    }
    return serviceBillingModel.isRecurring(service.billing) ? getRecurrencePeriodLabels(texts)[service.billing.recurrencePeriod] : texts.oneTimeSentence();
}
export function formatBillingTrigger(service, t) {
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    const isOneTimeInStages = serviceBillingModel.isOneTimeInStages(service.billing);
    if (isOneTimeInStages && oneTimeInStages) {
        return stagesTermsTextMap(t)[oneTimeInStages.terms()];
    }
    return isManual(service) ? 'Manual' : 'Automatic';
}
export function integratedId(param) {
    let { service, accounting } = param;
    switch(accounting){
        case Accounting.QBO:
            return service.integrations.qboIntegratedId();
        case Accounting.Xero:
            return service.integrations.xeroIntegratedId();
    }
}
export function limitedTimeDiscountOffer(service) {
    return isAwaitingApproval(service.status) ? service.status.awaitingApproval.limitedTimeDiscountOffer : undefined;
}
export function limitedTimeDiscountOfferExpirationType(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.DiscountExpirationType();
}
export function limitedTimeDiscountOfferExpirationDays(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.ExpirationDays();
}
export function limitedTimeDiscountOfferExpirationDate(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.ExpirationDate();
}
export function limitedTimeDiscountOfferExpiration(service) {
    const offer = limitedTimeDiscountOffer(service);
    return offer === null || offer === void 0 ? void 0 : offer.Expiration();
}
export function hasUnbillableStatus(service) {
    return serviceModel.matchStatus(service.status, {
        awaitingApproval: ()=>false,
        amended: ()=>false,
        approved: ()=>false,
        completed: ()=>true,
        terminated: ()=>true,
        canceled: ()=>true
    });
}
export function matchStatus(status, matcher) {
    switch(status.type){
        case ServiceStatusType.Amended:
            return matcher.amended(status);
        case ServiceStatusType.AwaitingApproval:
            return matcher.awaitingApproval(status);
        case ServiceStatusType.Completed:
            return matcher.completed(status);
        case ServiceStatusType.Terminated:
            return matcher.terminated(status);
        case ServiceStatusType.Canceled:
            return matcher.canceled(status);
        case ServiceStatusType.Approved:
            return matcher.approved(status);
    }
}
export function isBundle(service) {
    return !!service.bundle;
}
export function includedServices(service) {
    var _service_bundle;
    return ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) || null;
}
